import { IonCol, IonGrid, IonRow } from "@ionic/react";
import equal from "deep-equal";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { createEnumParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import RadioButtonGroup from "@components/fields/RadioButtonGroup";
import SearchInput, { SearchOptionGroup } from "@components/fieldsNative/SearchInput";
import Form from "@components/forms/Form";
import styles from "@components/forms/GoalTemplateFilters.module.css";
import { FocusAreaEnum } from "@typing/Generated";

type FormData = {
  focusArea?: FocusAreaEnum | null;
  searchTerm?: string | null;
};

type Props = {
  filters: FormData;
  onChange: (filterValues: FormData) => void;
  onSearch: ((input: string) => Promise<SearchOptionGroup[]>) | undefined;
  userIsExpert?: boolean;
};

const GoalFilters = ({ filters, onChange, onSearch }: Props) => {
  const intl = useIntl();

  const focusAreaParam = createEnumParam<FocusAreaEnum>(Object.values(FocusAreaEnum));

  const [query, setQuery] = useQueryParams({
    focusArea: withDefault({ ...focusAreaParam, urlName: "goalTemplateFocusArea" }, filters.focusArea),
    searchTerm: withDefault({ ...StringParam, urlName: "goalTemplateSearchTerm" }, filters.searchTerm)
  });

  const defaultValues: FormData = query;

  const form = useForm<FormData>({
    defaultValues,
    mode: "onSubmit"
  });

  const focusArea = form.watch("focusArea");
  const searchTerm = form.watch("searchTerm");

  useEffect(() => {
    // The select and radio button group for the same field switches focusArea right back to what it was, so this forces it
    form.setValue("focusArea", focusArea);

    const values = { focusArea, searchTerm };
    if (!equal(values, filters)) {
      setQuery(values);
      onChange(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, focusArea, searchTerm, onChange, setQuery]);

  const focusAreaOptionsWithNull = useMemo(() => {
    const options = Object.values(FocusAreaEnum).map(option => ({
      label: intl.formatMessage({ id: `models.focusArea.${option}` }),
      value: option as string | null
    }));
    options.unshift({ label: intl.formatMessage({ id: "dictionary.all" }), value: null });

    return options;
  }, [intl]);

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <Form hookForm={form} onSubmit={() => {}}>
      <div className={styles.selectAndInput}>
        <div className={styles.search}>
          <IonGrid className={styles.unpaddedGrid}>
            <IonRow>
              <IonCol size="12">
                <SearchInput
                  error={form.formState.errors.searchTerm?.message}
                  initialValue={defaultValues.searchTerm}
                  name="searchTerm"
                  onSearch={onSearch}
                  setValue={() => {}}
                />
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>
      </div>
      <div data-test="focus-area-filters">
        <RadioButtonGroup<FormData>
          control={form.control}
          error={form.formState.errors.focusArea}
          name="focusArea"
          noSpacing
          options={focusAreaOptionsWithNull}
        />
      </div>
    </Form>
  );
};

export default GoalFilters;
